import type { FieldSpec, InterviewTemplate } from "../templates/types";
import { normalize, normalizeForMatching } from "./normalize";
import { validate } from "./validate";
import type {
  ExtractionCandidate,
  FieldEvent,
  FieldState,
  FieldValue,
  InputMode,
  Locale,
  VerbatimEntry,
} from "./types";

const MIN_CONFIDENCE = 0.5;

export type MergeContext = {
  turnIndex: number;
  /** The participant message this turn's proposal was generated from. */
  latestParticipantMessage: string;
  /**
   * All participant messages currently in the visible transcript window
   * (chronological order, latest last) — what rule 2's quote-presence check
   * searches. Includes `latestParticipantMessage` as its final element.
   */
  recentParticipantMessages: string[];
  locale: Locale;
  inputMode: InputMode;
  /** ISO timestamp used for both provenance.extractedAt and the FieldEvent. */
  at: string;
};

export type MergeResult = {
  fields: Record<string, FieldState>;
  events: FieldEvent[];
};

function quotePresentIn(quote: string, messages: string[]): boolean {
  const needle = normalizeForMatching(quote);
  if (!needle) return false;
  return messages.some((message) => normalizeForMatching(message).includes(needle));
}

function quoteFromLatest(quote: string, latestMessage: string): boolean {
  const needle = normalizeForMatching(quote);
  if (!needle) return false;
  return normalizeForMatching(latestMessage).includes(needle);
}

function rejectionEvent(
  candidate: ExtractionCandidate,
  reason: string,
  context: MergeContext,
): FieldEvent {
  return {
    at: context.at,
    turnIndex: context.turnIndex,
    fieldId: candidate.field_id,
    kind: "rejected",
    quote: candidate.quote,
    confidence: candidate.confidence,
    reason,
  };
}

function appendVerbatim(
  existing: FieldState | undefined,
  candidate: ExtractionCandidate,
  context: MergeContext,
): { field: FieldState; event: FieldEvent } {
  const entry: VerbatimEntry = {
    turnIndex: context.turnIndex,
    lang: context.locale,
    text: candidate.quote,
    inputMode: context.inputMode,
  };
  const priorEntries = (existing?.value as VerbatimEntry[] | null) ?? [];
  const value: VerbatimEntry[] = [...priorEntries, entry];

  return {
    field: {
      value,
      status: "filled",
      provenance: {
        source: "llm_extraction",
        quote: candidate.quote,
        quoteTurnIndex: context.turnIndex,
        confidence: candidate.confidence,
        lang: context.locale,
        inputMode: context.inputMode,
        extractedAt: context.at,
        reviewed: false,
      },
      askedCount: existing?.askedCount ?? 0,
    },
    event: {
      at: context.at,
      turnIndex: context.turnIndex,
      fieldId: candidate.field_id,
      kind: "accepted",
      value,
      quote: candidate.quote,
      confidence: candidate.confidence,
    },
  };
}

function mergeOne(
  existing: FieldState | undefined,
  field: FieldSpec,
  candidate: ExtractionCandidate,
  context: MergeContext,
): { field: FieldState; event: FieldEvent } {
  const normalized = normalize(candidate.raw_value, field, context.locale);
  const outcome = validate(normalized, field);

  const belowConfidenceFloor = candidate.confidence < MIN_CONFIDENCE;

  // Rule 4: an existing FILLED value may only be overwritten by a candidate
  // whose quote comes from the latest message and whose confidence is at
  // least as high as what's already there. Otherwise the new value is
  // staged for one-time confirmation rather than silently replacing an
  // answer the participant already gave.
  const wouldOverwriteFilled = existing?.status === "filled";
  const overwriteAllowed =
    !wouldOverwriteFilled ||
    (quoteFromLatest(candidate.quote, context.latestParticipantMessage) &&
      candidate.confidence >= (existing?.provenance?.confidence ?? 0));

  let finalStatus = outcome.status;
  let kind: FieldEvent["kind"] = finalStatus === "filled" ? "accepted" : "staged";
  let reason: string | undefined;

  if (belowConfidenceFloor && outcome.status === "filled") {
    finalStatus = "needs_confirmation";
    kind = "staged";
    reason = "low_confidence";
  } else if (wouldOverwriteFilled && finalStatus === "filled" && !overwriteAllowed) {
    finalStatus = "needs_confirmation";
    kind = "staged";
    reason = "overwrite_confirmation_required";
  } else if (outcome.status === "needs_confirmation" && !reason) {
    reason = normalized === null ? "unmappable_value" : "out_of_range_or_invalid";
  }

  const value: FieldValue = outcome.value;

  return {
    field: {
      value,
      status: finalStatus,
      provenance: {
        source: "llm_extraction",
        quote: candidate.quote,
        quoteTurnIndex: context.turnIndex,
        confidence: candidate.confidence,
        lang: context.locale,
        inputMode: context.inputMode,
        extractedAt: context.at,
        reviewed: false,
      },
      askedCount: existing?.askedCount ?? 0,
    },
    event: {
      at: context.at,
      turnIndex: context.turnIndex,
      fieldId: candidate.field_id,
      kind,
      value,
      quote: candidate.quote,
      confidence: candidate.confidence,
      reason,
    },
  };
}

/**
 * The anti-hallucination layer (§8). Every extraction candidate the model
 * proposes passes through here before it can touch state, in this order:
 *
 *   1. Drop any extraction whose field_id is not in the active template.
 *   2. Drop any extraction whose quote is not present (normalized substring)
 *      in the participant's recent messages. THIS IS THE SINGLE MOST
 *      IMPORTANT RULE IN THIS PROJECT: no quote in the transcript, no
 *      structured value. It is why a hallucinated value cannot reach an
 *      export — the model must point at words the participant actually
 *      typed. Do not relax it to "quote optional when confidence is high,"
 *      and do not soften the substring check into fuzzy matching.
 *      Normalization for *matching* operates on a throwaway copy
 *      (normalizeForMatching) and never writes back — the stored quote is
 *      always the participant's original text.
 *   3. confidence < 0.5 -> stage as needs_confirmation, do not commit.
 *   4. Overwriting an existing FILLED value requires the new quote to come
 *      from the latest message AND confidence >= the existing value's;
 *      otherwise the candidate is staged for one-time confirmation instead
 *      of silently replacing an answer already given.
 *   5. Every accepted, staged, AND rejected candidate gets a FieldEvent in
 *      the audit log, including the rejection reason.
 */
export function mergeExtractions(
  fields: Record<string, FieldState>,
  template: InterviewTemplate,
  extractions: ExtractionCandidate[],
  context: MergeContext,
): MergeResult {
  const fieldsById = new Map(template.fields.map((f) => [f.id, f]));
  const nextFields: Record<string, FieldState> = { ...fields };
  const events: FieldEvent[] = [];

  for (const candidate of extractions) {
    const field = fieldsById.get(candidate.field_id);

    // Rule 1: unknown field_id. The JSON-schema layer can only check syntax
    // (Contracts::TurnProposal / turn.ts additionally allowlist against the
    // template server- and client-side); this is the last line of defense
    // and must reject unconditionally.
    if (!field) {
      events.push(rejectionEvent(candidate, "unknown_field_id", context));
      continue;
    }

    // Rule 2: quote-presence. Checked before anything else about the
    // candidate's content — an unanchored claim is discarded outright, not
    // merely down-weighted.
    if (!quotePresentIn(candidate.quote, context.recentParticipantMessages)) {
      events.push(rejectionEvent(candidate, "quote_not_in_transcript", context));
      continue;
    }

    const existing = nextFields[field.id];
    const { field: updated, event } =
      field.type === "verbatim"
        ? appendVerbatim(existing, candidate, context)
        : mergeOne(existing, field, candidate, context);

    nextFields[field.id] = updated;
    events.push(event);
  }

  return { fields: nextFields, events };
}
